<template>
  <div :class="props.height ? 'h-full flex flex-col' : ''" :style="props.height ? { height: props.height } : {}">
    <CalendarToolbar
      :date-iso="currentDayIso"
      :date-label-long="dayLabel"
      :date-label-short="dayLabelShort"
      :keep-long-date="!!lockedTeacher"
      :hide-tab-switcher="hideTabSwitcher"
      :center-nav="centerNav"
      :locked-teacher="lockedTeacher"
      :all-teachers="allTeachers"
      :all-instruments="allInstruments"
      :all-locations="allLocations"
      :active-teachers="activeTeachers"
      :selected-location="selectedLocation"
      :is-dark="isDark"
      :is-instrument-active="isInstrumentActive"
      :is-instrument-partial="isInstrumentPartial"
      :is-loading="isLoading"
      :readonly="readonly"
      @prev="prevDay"
      @next="nextDay"
      @jump-date="jumpToDate"
      @today="jumpToday"
      @refresh="refresh"
      @new-lesson="openCreateModal({ date: currentDayIso, teacher: lockedTeacher, time: '' })"
      @toggle-teacher="toggleTeacher"
      @deselect-all="deselectAll"
      @toggle-instrument="toggleInstrument"
      @select-location="selectLocation"
    >
      <template #extra-controls>
        <slot name="extra-controls" />
      </template>
    </CalendarToolbar>

    <ScheduleNoticeBar v-if="!readonly" />

    <!-- Loading -->
    <div v-if="isLoading && !lessons.length" class="flex items-center justify-center py-20">
      <svg class="animate-spin h-6 w-6 text-fg-faint" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
      </svg>
    </div>

    <!-- Time grid: one day fills the viewport, scroll/swipe sideways to page through neighboring days.
         Radius/clip live on this outer, non-scrolling wrapper — Chrome doesn't reliably clip
         `position: sticky` children to a border-radius on the element that also scrolls, so the
         scrollable div (ref="scrollContainer") stays plain and unrounded inside it. When a
         `height` prop is given, this wrapper fills the flex-column root exactly (flex-1 min-h-0)
         instead of taking an explicit height itself — a percentage height here would need the
         root to have a definite height to resolve against, and re-deriving that in two places
         is exactly the kind of drift that clipped the rounded corner before. -->
    <div
      v-else
      data-tour="calendar-grid"
      class="rounded-lg border border-stroke dark:border-stroke-dark overflow-hidden bg-calendar-bg dark:bg-calendar-bg-dark"
      :class="props.height ? 'flex-1 min-h-0' : ''"
    >
    <div
      ref="scrollContainer"
      data-tour="calendar-viewport"
      class="h-full overflow-auto"
      :style="props.height ? {} : { maxHeight: props.maxHeight }"
    >
      <div style="width: max-content">

        <!-- Sticky header -->
        <div class="sticky top-0 z-30 border-b border-stroke dark:border-stroke-dark"
             :style="{ display: 'grid', gridTemplateColumns: gridCols }">

          <!-- Time gutter placeholder — drag to resize row height. Locked-teacher view has no
               teacher sub-header row (see below), so it only spans row 1. -->
          <div class="group sticky left-0 z-40 flex items-end justify-center cursor-ns-resize select-none bg-surface-raised dark:bg-surface-raised-dark border-r border-stroke dark:border-stroke-dark"
               data-tour="hour-height-handle"
               :style="{ gridRow: lockedTeacher ? '1' : '1 / 3', gridColumn: 1, width: TIME_COL_WIDTH + 'px', touchAction: 'none' }"
               title="Drag to resize rows"
               @pointerdown="startHourHeightResize">
            <ArrowsUpDownIcon class="w-4 h-4 mb-1 text-fg-muted dark:text-fg-muted-dark group-hover:text-blue-500 transition-colors" />
          </div>

          <!-- Day headers: the outer cell spans the day's FULL width (all its teacher
               columns combined) via gridColumnEnd — with several teachers selected on a
               narrow screen that can be much wider than the viewport, and the date would
               scroll off-screen with the rest of the cell if it just sat at its natural
               (left) position. The inner content is `sticky` with its own `left` offset
               instead, so it stays pinned just right of the time gutter as long as any
               part of this day's cell is still in view — a second, nested sticky axis
               under the row's own `sticky top-0` (the same technique spreadsheet apps use
               for sticky sub-headers under an already-sticky top row). This requires the
               outer cell to NOT establish its own scroll/clip context (an ancestor with
               `overflow: hidden` becomes the sticky element's containing scrollport
               instead of the real scrollContainer, which would silently stop it from
               tracking scroll at all) — the overflow-hidden that used to live on the
               outer cell moved down onto the inner element, which is now sized to
               COL_WIDTH (matching a single-teacher day) instead of the full span. -->
          <div
            v-for="(day, di) in activeDayGroups" :key="'dh-' + day.iso"
            :ref="el => setDayHeaderRef(day.iso, el)"
            class="relative flex items-center border-b border-stroke dark:border-stroke-dark bg-surface-raised dark:bg-surface-raised-dark"
            :class="[
              di < activeDayGroups.length - 1 ? 'border-r-2 border-r-stroke-strong dark:border-r-stroke-strong-dark' : ''
            ]"
            :style="{ gridRow: 1, gridColumnStart: day.gridColStart, gridColumnEnd: 'span ' + day.gridColSpan }"
          >
            <div class="dv-day-header-inner sticky flex items-baseline justify-center gap-1 sm:block text-center px-1 py-1.5 sm:py-2 overflow-hidden"
                 :style="{ '--dv-header-left': `calc(50vw - ${(COL_WIDTH - TIME_COL_WIDTH) / 2}px)`, width: COL_WIDTH + 'px' }">
              <div class="text-[10px] sm:text-xs font-medium uppercase tracking-wide text-fg-muted dark:text-fg-muted-dark">{{ day.weekday }}</div>
              <div class="flex justify-center">
                <span :class="['inline-flex items-center justify-center rounded-full font-bold text-base sm:text-xl leading-none transition-colors',
                  day.isToday ? 'bg-blue-600 text-white w-7 h-7 sm:w-9 sm:h-9' : 'text-fg-base dark:text-fg-base-dark']">{{ day.dayNum }}</span>
              </div>
            </div>
            <!-- Locked-teacher view: no name sub-header needed (it's the teacher's own name),
                 but keep the gradient underline as a quiet "you're scheduled this day" cue.
                 Stays on the outer (full-width) cell — locked-teacher days are always a
                 single column anyway, so this never actually needs to track scroll. -->
            <div v-if="lockedTeacher && day.cols[0]?.availability"
                 class="absolute bottom-0 left-2 right-2 h-[2px] rounded-t opacity-70" :style="dotStyle(lockedTeacher)" />
          </div>

          <!-- Teacher sub-headers (multi-teacher views only — a locked-teacher view already
               shows the teacher's own name in the page header, so this row is redundant) -->
          <template v-if="!lockedTeacher">
          <template v-for="(day, di) in activeDayGroups" :key="'ts-' + day.iso">
            <template v-if="day.cols.length">
              <div
                v-for="(col, ci) in day.cols" :key="col.teacher"
                class="relative flex flex-col items-center justify-center gap-1 px-1 py-2.5 bg-surface dark:bg-surface-raised-dark border-r border-stroke dark:border-stroke-dark"
                :class="ci === day.cols.length - 1 && di < activeDayGroups.length - 1 ? 'border-r-2 border-r-stroke-strong dark:border-r-stroke-strong-dark' : ''"
                :style="{ gridRow: 2, gridColumnStart: col.gridCol }"
              >
                <span class="w-2 h-2 rounded-full shrink-0" :style="dotStyle(col.teacher)" />
                <span class="text-xs font-semibold text-fg-secondary dark:text-fg-base-dark truncate w-full text-center leading-tight">{{ col.teacher.split(' ')[0] }}</span>
                <div v-if="col.availability"
                     class="absolute bottom-0 left-2 right-2 h-[2px] rounded-t opacity-70" :style="dotStyle(col.teacher)" />
              </div>
            </template>
            <div v-else
                 class="bg-surface-raised dark:bg-surface-raised-dark"
                 :class="di < activeDayGroups.length - 1 ? 'border-r-2 border-r-stroke-strong dark:border-r-stroke-strong-dark' : ''"
                 :style="{ gridRow: 2, gridColumnStart: day.gridColStart }" />
          </template>
          </template>

        </div>

        <!-- Body grid -->
        <div :style="{ display: 'grid', gridTemplateColumns: gridCols, height: bodyHeight + 'px' }">

          <!-- Time gutter: z-[25], strictly between the now-line's z-20 in DayTimeGrid (so the
               red line tucks behind the hour labels) and the sticky header's z-30 (equal z-index
               there was a tie broken by DOM order — the body grid comes after the header, so at
               z-30 this gutter painted over the header, including the resize-handle corner,
               blocking its pointer events and breaking vertical zoom). -->
          <div class="sticky left-0 z-[25] bg-surface dark:bg-surface-raised-dark border-r border-stroke dark:border-stroke-dark relative"
               :style="{ width: TIME_COL_WIDTH + 'px', height: bodyHeight + 'px' }">
            <div v-for="h in displayHours" :key="h"
                 class="absolute right-2 text-[10px] text-fg-faint dark:text-fg-faint-dark leading-none"
                 :style="{ top: (hourToY(h, computedMinHour) - 5) + 'px' }">
              {{ fmtHour(h) }}
            </div>
          </div>

          <!-- Day body cells -->
          <template v-for="(day, di) in activeDayGroups" :key="day.iso">

            <template v-if="day.cols.length">
              <DayTimeGrid
                v-for="(col, ci) in day.cols" :key="col.teacher"
                :day="day"
                :col="col"
                :lessons="lessonsForCol(col.teacher, day.iso)"
                :time-off-blocks="timeOffBlocksFor(col.teacher, day.iso)"
                :ghosts="ghostsForCol(col.teacher, day.iso)"
                :can-manage-invites="isAdmin"
                :min-hour="computedMinHour"
                :max-hour="computedMaxHour"
                :is-dark="isDark"
                :color-by-teacher="!!lockedTeacher"
                :readonly="readonly || (!hasSignupsPermission && !lockedTeacher && col.teacher !== currentUser?.name)"
                @create-lesson="openCreateModal"
                @mark-time-off="openTimeOffModal"
                @lessons-removed="removeLessons"
                @time-off-deleted="removeCancellation"
                @ghosts-changed="refresh"
                @open-request="openRequest = $event"
                class="border-r border-stroke dark:border-stroke-dark"
                :class="ci === day.cols.length - 1 && di < activeDayGroups.length - 1 ? 'border-r-2 border-r-stroke-strong dark:border-r-stroke-strong-dark' : ''"
                :style="{ gridColumnStart: col.gridCol }"
              />
            </template>

            <!-- Empty day -->
            <div v-else
                 class="relative bg-surface-raised/40 dark:bg-surface-dark/20"
                 :class="di < activeDayGroups.length - 1 ? 'border-r-2 border-r-stroke-strong dark:border-r-stroke-strong-dark' : ''"
                 :style="{ gridColumnStart: day.gridColStart, height: bodyHeight + 'px' }">
              <div v-for="h in allHours" :key="h"
                   class="absolute left-0 right-0 border-t border-stroke-subtle dark:border-stroke-dark/30 pointer-events-none"
                   :style="{ top: hourToY(h, computedMinHour) + 'px' }" />
            </div>

          </template>
        </div>

      </div>
    </div>
    </div>
  </div>

  <LessonCreateModal
    v-if="createProps"
    v-bind="createProps"
    @close="createProps = null"
    @saved="() => { createProps = null; refresh() }"
  />

  <RequestCardModal v-if="openRequest" :signup="openRequest" @close="openRequest = null" @updated="openRequest = null; refresh()" />

  <TeacherCancellationModal
    v-if="showTimeOffModal"
    :initial-date="timeOffDate"
    :initial-time="timeOffTime"
    :teacher-name="timeOffTeacher"
    @close="showTimeOffModal = false; refresh()"
  />
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import { ArrowsUpDownIcon } from '@heroicons/vue/24/outline'
import RequestCardModal from '@/components/calendar/RequestCardModal.vue'
import { useRequestGhosts } from '@/composables/useRequestGhosts.js'
import { useApi } from '@/composables/useApi.js'
import {
  hourHeight, TIME_COL_WIDTH, COL_WIDTH, startHourHeightResize,
  hourToY, parseLessonTime, localIso, easternToday,
  fmtHour, fitHourHeightToContainer, isLessonExcused, lessonsChange,
} from '@/composables/useCalendarData.js'
import { useCalendarFilters } from '@/composables/useCalendarFilters.js'
import { useNow } from '@/composables/useNow.js'
import { avatarGradient } from '@/composables/useAvatarGradient.js'
import DayTimeGrid from '@/components/calendar/DayTimeGrid.vue'
import ScheduleNoticeBar from '@/components/calendar/ScheduleNoticeBar.vue'
import CalendarToolbar from '@/components/calendar/CalendarToolbar.vue'
import LessonCreateModal from '@/components/common/LessonCreateModal.vue'
import TeacherCancellationModal from '@/components/common/TeacherCancellationModal.vue'
import { useDemoMode } from '@/composables/useDemoMode'
import { useAdminSidebar } from '@/composables/useAdminSidebar.js'

const currentUser = JSON.parse(localStorage.getItem('user') || 'null')
const effectivePermissions = useDemoMode().effectivePermissionsFor(() => currentUser?.permissions)
const isAdmin = computed(() => effectivePermissions.value.includes('admin'))
const hasSignupsPermission = computed(() => effectivePermissions.value.includes('admin') || effectivePermissions.value.includes('lesson_signups'))

// Admin pages open the form in the admin sidebar; the teacher dashboard's embed as a popup.
const adminSidebar = useAdminSidebar()
const createProps = ref(null)
const openCreateModal = ({ date, teacher, time }) => {
  const p = {
    initialDate:          date,
    initialTime:          time || '',
    lockedTeacherName:    hasSignupsPermission.value ? null : (props.lockedTeacher || currentUser?.name),
    lockedTeacherId:      hasSignupsPermission.value ? null : currentUser?.id,
    canManageSignups:     hasSignupsPermission.value,
    preselectTeacherName: teacher || '',
  }
  if (adminSidebar.available.value) adminSidebar.open('lesson-create', p, { onSaved: refresh })
  else createProps.value = p
}

const showTimeOffModal = ref(false)
const timeOffDate = ref('')
const timeOffTime = ref('')
const timeOffTeacher = ref('')
const openTimeOffModal = ({ date, teacher, time }) => {
  timeOffDate.value = date
  timeOffTime.value = time || ''
  timeOffTeacher.value = teacher || ''
  showTimeOffModal.value = true
}

const DAY_STEP = 3  // days mounted/fetched per lazy-load step, and per initial ± window

const props = defineProps({
  lessonEndpoint: { type: String, default: '/api/admin/calendar' },
  lockedTeacher:  { type: String, default: null },  // when set: single teacher, no filter bar
  maxHeight:      { type: String, default: 'min(90vh, 2000px)' },  // CSS length — caps the box, but it can render shorter if content is shorter
  height:         { type: String, default: null },    // CSS length — forces the box to exactly this size regardless of content (for resizable panels)
  hideTabSwitcher: { type: Boolean, default: false },  // suppress the router-based Day/Week/Month select (parent supplies its own)
  centerNav:       { type: Boolean, default: false },  // center the date/prev/next controls row instead of left-aligning it
  noAutoFit:       { type: Boolean, default: false },  // skip the one-shot "zoom hourHeight to fill the container" — for a tall,
                                                        // near-full-viewport container this can zero out overflow entirely,
                                                        // leaving nothing to scroll to
  readonly:        { type: Boolean, default: false },  // suppress click-to-create-lesson (read-only embed)
  hideExcused:     { type: Boolean, default: false },  // teacher preference: drop excused lessons from the grid (still visible in Attendance tab)
  showRequests:    { type: Boolean, default: false },  // teacher's own calendar: open lesson requests as ghosts (useRequestGhosts.js)
  initialDate:     { type: String, default: null },     // ISO date to land on at mount (e.g. clicked through from an embedded MonthView); overrides ?date=
})

const { fetchApi } = useApi()

// ── Dark mode ─────────────────────────────────────────────────────────────────
const isDark = ref(document.documentElement.classList.contains('dark'))
let _darkObserver
onMounted(() => {
  _darkObserver = new MutationObserver(() => {
    isDark.value = document.documentElement.classList.contains('dark')
  })
  _darkObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
})
onUnmounted(() => _darkObserver?.disconnect())

const dotStyle = name => ({ background: avatarGradient(name) })

// A specific date to land on, e.g. clicked through from Month view (?date=2026-07-22)
const route = useRoute()
const queryDateIso = /^\d{4}-\d{2}-\d{2}$/.test(route.query.date || '') ? route.query.date : null

// ── Rolling day window (not week-aligned) ────────────────────────────────────
const todayIso = localIso(easternToday())
const loadedStart = ref(new Date())  // set in initialLoad()
const loadedEnd   = ref(new Date())

const mountedDays = computed(() => {
  const days = []
  const cursor = new Date(loadedStart.value)
  while (cursor <= loadedEnd.value) {
    const iso = localIso(cursor)
    days.push({
      iso,
      weekday:     cursor.toLocaleDateString('en-US', { weekday: 'short' }),
      weekdayLong: cursor.toLocaleDateString('en-US', { weekday: 'long' }),
      dayNum:      cursor.getDate(),
      month:       cursor.toLocaleDateString('en-US', { month: 'short' }),
      isToday:     iso === todayIso,
    })
    cursor.setDate(cursor.getDate() + 1)
  }
  return days
})

// ── Data ──────────────────────────────────────────────────────────────────────
const lessons       = ref([])
const teachers      = ref([])
const cancellations = ref([])  // marked time-off windows: [{ teacher_name, start_datetime, end_datetime, reason }]
const ghosts        = ref([])  // pending invites, drawn as unconfirmed lessons (`${lessonEndpoint}-ghosts`)
const { requestGhosts, fetchRequests } = useRequestGhosts()
const openRequest   = ref(null)  // signup whose card is open
const isLoading     = ref(false)
let teachersFetched = false

// Drops locally-known lessons (e.g. ones soft-deleted by an "end series" edit)
// without a refetch — see cancelled_lesson_ids returned from /lesson-edits.
const removeLessons = (ids) => {
  if (!ids?.length) return
  const idSet = new Set(ids)
  lessons.value = lessons.value.filter(l => !idSet.has(l.id))
}

// Locked-teacher (own dashboard) callers get just their own availability from the
// self-service endpoint. `as_teacher` is honored server-side only for admins (Demo Mode
// preview); a real teacher passing their own name is a no-op, so it's always safe to
// include. Non-locked callers (admin calendar, Team Calendar overview) use the
// team-wide roster endpoint, which — unlike /api/admin/teachers — isn't admin-gated,
// so it also works for a non-admin teacher browsing Team Calendar.
const fetchTeachersOnce = async () => {
  if (teachersFetched) return
  teachersFetched = true
  try {
    teachers.value = props.lockedTeacher
      ? [await fetchApi(`/api/teacher-availability/me?as_teacher=${encodeURIComponent(props.lockedTeacher)}`)
          .then(r => ({ name: r.teacher_name, availability: r.availability, instruments: r.instruments }))]
      : await fetchApi('/api/teacher-availability/roster')
  } catch { teachersFetched = false }
}

// App.vue's primary (desktop) calendar embed isn't gated on auth having resolved, so this
// component can mount — and fire its first fetchTeachersOnce() — before `previewName` is
// known, i.e. while lockedTeacher is still falsy. Once lockedTeacher resolves to a real
// name, retry so the view narrows to that teacher's own /me record — a no-op if the first
// (roster) attempt already succeeded.
watch(() => props.lockedTeacher, name => { if (name) fetchTeachersOnce() })

// Merge (dedupe by id) unless replace: true, which is used for the initial load and manual refresh
const fetchRange = async (startIso, endIso, { replace = false } = {}) => {
  const range = `?start_date=${startIso}&end_date=${endIso}`
  const [data, ghostData, requestData] = await Promise.all([
    fetchApi(`${props.lessonEndpoint}${range}`),
    fetchApi(`${props.lessonEndpoint}-ghosts${range}`).catch(() => []),
    // Admin calendar: every teacher's open requests, date-scoped like invites
    props.showRequests ? [] : fetchApi(`${props.lessonEndpoint}-request-ghosts${range}`).catch(() => []),
  ])
  const merge = (list, add) => {
    const seen = new Set(list.map(l => l.id))
    return [...list, ...add.filter(l => !seen.has(l.id))]
  }
  lessons.value = replace ? data : merge(lessons.value, data)
  ghosts.value = replace ? [...ghostData, ...requestData] : merge(ghosts.value, [...ghostData, ...requestData])
  // Not date-scoped (a teacher's open requests are few), so only on a full load.
  if (replace && props.showRequests) await fetchRequests(props.lockedTeacher || currentUser?.name)
}

// Same merge-or-replace convention as fetchRange, deduped by teacher+start since
// TeacherCancellation rows have no client-visible id from this lightweight endpoint.
const fetchCancellationsRange = async (startIso, endIso, { replace = false } = {}) => {
  const data = await fetchApi(`/api/teacher-cancellations/range?start_date=${startIso}T00:00:00&end_date=${endIso}T23:59:59`).catch(() => [])
  if (replace) {
    cancellations.value = data
  } else {
    const seen = new Set(cancellations.value.map(c => `${c.teacher_name}::${c.start_datetime}`))
    cancellations.value = [...cancellations.value, ...data.filter(c => !seen.has(`${c.teacher_name}::${c.start_datetime}`))]
  }
}

// Clip a (possibly multi-day) cancellation window down to "HH:MM"–"HH:MM" for one day,
// same convention useCalendarData.js uses for its own timeOffBlocksFor.
const timeOffBlocksFor = (teacherName, iso) => {
  const dayStart = new Date(`${iso}T00:00:00`)
  const dayEnd   = new Date(`${iso}T23:59:59`)
  const fmt = d => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
  return cancellations.value
    .filter(c => c.teacher_name === teacherName)
    .map(c => {
      const start = new Date(c.start_datetime)
      const end   = new Date(c.end_datetime)
      if (end <= dayStart || start >= dayEnd) return null
      const clipStart = start < dayStart ? dayStart : start
      const clipEnd   = end > dayEnd ? dayEnd : end
      return { id: c.id, start: fmt(clipStart), end: end > dayEnd ? '23:59' : fmt(clipEnd), teacherName, reason: c.reason }
    })
    .filter(Boolean)
}

// Drops a locally-known cancellation (e.g. after the user deletes it from the
// calendar) without a refetch — same convention as removeLessons above.
const removeCancellation = (id) => {
  cancellations.value = cancellations.value.filter(c => c.id !== id)
}

const teacherMap = computed(() => new Map(teachers.value.map(t => [t.name, t])))
const allTeachers = computed(() => {
  const names = new Set([
    ...teachers.value.map(t => t.name),
    ...lessons.value.map(l => l.teacher_name).filter(Boolean),
  ])
  return [...names].sort()
})
const allInstruments = computed(() => {
  const insts = new Set()
  teachers.value.forEach(t => (t.instruments || []).forEach(i => insts.add(i)))
  return [...insts].sort()
})
const allLocations = computed(() => {
  const locs = new Set()
  lessons.value.forEach(l => { if (l.location) locs.add(l.location) })
  const sorted = [...locs].sort()
  if (lessons.value.some(l => !l.location)) sorted.unshift('__none__')
  return sorted
})

// ── Filters ───────────────────────────────────────────────────────────────────
const {
  activeTeachers, selectedLocation,
  toggleTeacher, deselectAll,
  toggleInstrument, isInstrumentActive, isInstrumentPartial,
  selectLocation, matchesLocation,
  filteredTeachers: filteredTeachersRaw,
} = useCalendarFilters({ allTeachers, teacherMap, shared: !props.lockedTeacher })

const filteredTeachers = computed(() => props.lockedTeacher ? [props.lockedTeacher] : filteredTeachersRaw.value)

const lessonsForCol = (teacher, iso) =>
  lessons.value
    .filter(l => l.lesson_date === iso && l.teacher_name === teacher && matchesLocation(l))
    .filter(l => !props.hideExcused || !isLessonExcused(l))
    .sort((a, b) => (a.lesson_time || '').localeCompare(b.lesson_time || ''))

// ── Day paging with free horizontal scroll + lazy-loaded rolling window ──────
const currentDayIso = ref(props.initialDate || queryDateIso || todayIso)
const currentDay = computed(() => mountedDays.value.find(d => d.iso === currentDayIso.value) || mountedDays.value[0])
// Last day at least half in view (currentDayIso is the first) — the toolbar shows the range.
// null/same-as-first → single-day label.
const lastVisibleIso = ref(null)
const rangeEnd = computed(() =>
  currentDay.value && lastVisibleIso.value > currentDay.value.iso ? new Date(lastVisibleIso.value + 'T00:00:00') : null)
const dayLabel = computed(() => {
  if (!currentDay.value) return ''
  const s = new Date(currentDay.value.iso + 'T00:00:00')
  const e = rangeEnd.value
  if (!e) return s.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
  if (s.getFullYear() !== e.getFullYear())
    return `${s.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} – ${e.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
  if (s.getMonth() === e.getMonth())
    return `${s.toLocaleDateString('en-US', { month: 'long' })} ${s.getDate()}–${e.getDate()}, ${e.getFullYear()}`
  return `${s.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${e.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${e.getFullYear()}`
})
// Narrow-toolbar fallback for dayLabel — see .dv-date-long/.dv-date-short below.
const dayLabelShort = computed(() => {
  if (!currentDay.value) return ''
  const d = new Date(currentDay.value.iso + 'T00:00:00')
  const e = rangeEnd.value
  if (e) return `${d.getMonth() + 1}/${d.getDate()}–${e.getMonth() + 1}/${e.getDate()}/${String(e.getFullYear()).slice(-2)}`
  return `${d.getMonth() + 1}/${d.getDate()}/${String(d.getFullYear()).slice(-2)}`
})

const scrollContainer = ref(null)
const dayHeaderEls = {}
const setDayHeaderRef = (iso, el) => {
  if (el) dayHeaderEls[iso] = el
  else delete dayHeaderEls[iso]
}

const scrollToDay = (iso, instant = false) => {
  const el = dayHeaderEls[iso]
  el?.scrollIntoView({ behavior: instant ? 'auto' : 'smooth', inline: 'start', block: 'nearest' })
}

// Programmatic navigation (arrows / date picker / today) races the label-sync observer below:
// scrollIntoView's completion isn't synchronously observable — even "instant" smooth-scrolling
// takes more than one frame — and the observer's callback can land mid-flight and report a
// stale/transient day, overwriting currentDayIso right after we set it correctly. This is the
// same race virtualized/paginated scrollers (Embla, Swiper, etc.) all hit, and they all solve it
// the same way: suppress passive "what's visible" tracking while a programmatic scroll is in
// flight, and only trust it again once the scroll has genuinely settled. `scrollend` is the
// browser-native signal for exactly that — it fires once a scroll (smooth or instant) has
// actually finished; the timeout is just a safety net for the case where the target was already
// the current position and no scroll (hence no scrollend) occurs.
let suppressLabelSync = false
const navigateToDay = iso => {
  suppressLabelSync = true
  currentDayIso.value = iso
  lastVisibleIso.value = null
  scrollToDay(iso, true)
  const el = scrollContainer.value
  const clearSuppression = () => {
    if (!suppressLabelSync) return
    suppressLabelSync = false
    el?.removeEventListener('scrollend', clearSuppression)
    updateCurrentDayFromScroll()  // scroll has settled — pick up the range end
  }
  el?.addEventListener('scrollend', clearSuppression, { once: true })
  setTimeout(clearSuppression, 400)
}

// Vertical scroll: with a fixed 0–24h axis the grid is usually taller than the box,
// so land on "now" (today) or the day's earliest lesson (other days) instead of hour 0.
const { nowHourFraction } = useNow()
const scrollToRelevantTime = iso => {
  if (!scrollContainer.value) return
  let targetHour = 8
  if (iso === todayIso) {
    targetHour = nowHourFraction.value
  } else {
    let earliest = null
    for (const l of lessons.value) {
      if (l.lesson_date !== iso) continue
      const p = parseLessonTime(l.lesson_time)
      if (p) earliest = earliest === null ? p.h : Math.min(earliest, p.h)
    }
    if (earliest !== null) targetHour = earliest
  }
  scrollContainer.value.scrollTop = Math.max(0, (targetHour - 1) * hourHeight.value)
}

// Zoom-anchor fix: dragging the row-height handle changes hourHeight, which rescales
// every y-position from the top of the grid. Without this, whatever's on screen drifts
// as you drag. Keep the hour at viewport-center stationary instead. (flush: 'post' so
// the DOM has already resized to the new bodyHeight before we touch scrollTop.)
watch(hourHeight, (newH, oldH) => {
  const el = scrollContainer.value
  if (!el || !oldH) return
  const viewportH = el.clientHeight
  el.scrollTop = (el.scrollTop + viewportH / 2) * (newH / oldH) - viewportH / 2
}, { flush: 'post' })

// Label-sync: tracks whichever mounted day is at the scroll container's leading (left) edge,
// which is what inline:'start' alignment (scrollToDay) actually targets. This replaced an
// IntersectionObserver-based "most visible among a batch of entries" approach, which went
// through several rounds of narrower and narrower race-condition fixes (recreating the observer
// per extend, suppressing it during programmatic scrolls, etc.) without ever actually being
// reliable — IntersectionObserver notifications are async, batched, and threshold-based, which
// gives no hard guarantee about exactly when a given scroll's true resting state gets reported,
// so an update can still land after the read that computes the *next* day-step and throw the
// arrow off by however many days the observer's view of "current" had already drifted.
// Carousel implementations that need to reliably know "which page am I on" (Embla, Swiper, native
// scroll-marker patterns) don't lean on IntersectionObserver for this reason — they read
// scrollLeft directly against each page's real offset, which is synchronous and exact: no
// batching, no thresholds, no ambiguity about which of several entries to trust.
const updateCurrentDayFromScroll = () => {
  if (suppressLabelSync) return
  const el = scrollContainer.value
  if (!el) return
  // The sticky time gutter (width TIME_COL_WIDTH) overlaps the start of the scrollable content,
  // so a day's real visible left edge in the viewport is TIME_COL_WIDTH further right than
  // scrollLeft. Comparing raw offsetLeft against scrollLeft without adding that back
  // consistently attributed the current position to the *previous* day.
  const viewLeft = el.scrollLeft + TIME_COL_WIDTH
  const viewRight = el.scrollLeft + el.clientWidth
  // Days at least half in view — a sliver peeking in at either edge doesn't count. A day wider
  // than the viewport counts once it fills half the viewport. After an arrow/jump the target is
  // aligned to the left edge (fully in view), so it's still the first one picked.
  let firstIso = null, lastIso = null
  for (const day of mountedDays.value) {
    const dayEl = dayHeaderEls[day.iso]
    if (!dayEl) continue
    const left = dayEl.offsetLeft, width = dayEl.offsetWidth
    const shown = Math.min(left + width, viewRight) - Math.max(left, viewLeft)
    if (shown < Math.min(width, viewRight - viewLeft) / 2 - 4) continue
    if (!firstIso || day.iso < firstIso) firstIso = day.iso
    if (!lastIso || day.iso > lastIso) lastIso = day.iso
  }
  if (firstIso) {
    currentDayIso.value = firstIso
    lastVisibleIso.value = lastIso
  }
}

let scrollRafId = null
const onContainerScroll = () => {
  if (scrollRafId !== null) return
  scrollRafId = requestAnimationFrame(() => {
    scrollRafId = null
    updateCurrentDayFromScroll()
  })
}
// Touch tracking: mutating scrollLeft from JS while a finger is actively down fights the
// browser's own native touch-scroll tracking, which is driving that same property every
// frame. That fight is what produced the "jumps around like crazy" symptom during a slow
// drag, even after the stale-scrollLeft bug below was fixed. extendBefore's fetch can still
// run immediately (it only populates data, not the DOM), but the actual column-prepend +
// scrollLeft correction needs to wait until the touch lifts.
let touchActive = false
const onTouchStart = () => { touchActive = true }
const onTouchEnd = () => { touchActive = false }
const waitForTouchEnd = el => new Promise(resolve => {
  if (!el || !touchActive) return resolve()
  const handler = () => {
    el.removeEventListener('touchend', handler)
    el.removeEventListener('touchcancel', handler)
    resolve()
  }
  el.addEventListener('touchend', handler)
  el.addEventListener('touchcancel', handler)
})

const resizeObserver = new ResizeObserver(() => onContainerScroll())
watch(scrollContainer, (el, oldEl) => {
  if (oldEl) resizeObserver.unobserve(oldEl)
  if (el) resizeObserver.observe(el)
  oldEl?.removeEventListener('scroll', onContainerScroll)
  el?.addEventListener('scroll', onContainerScroll, { passive: true })
  oldEl?.removeEventListener('touchstart', onTouchStart)
  oldEl?.removeEventListener('touchend', onTouchEnd)
  oldEl?.removeEventListener('touchcancel', onTouchEnd)
  el?.addEventListener('touchstart', onTouchStart, { passive: true })
  el?.addEventListener('touchend', onTouchEnd, { passive: true })
  el?.addEventListener('touchcancel', onTouchEnd, { passive: true })
})

// Edge-extend observer: watches the first/last mounted day headers, with a lookahead margin
// so more days get fetched/mounted before the user hits the wall. The margin must stay smaller
// than one DAY_STEP's worth of content (DAY_STEP * COL_WIDTH) — refreshObservers() tears down
// and recreates this observer after every extension, and re-observing a target queues a fresh
// intersection check against its *current* position. If a single extension doesn't push the
// edge day outside the margin, that fresh check immediately fires another extension, which
// fires another — a cascade of several extends in a burst, racing whatever navigation
// (shiftDay/jumpToDate) triggered it and landing on a non-deterministic day.
const EDGE_LOOKAHEAD_PX = 200
let edgeObserver = null
const setupEdgeObserver = () => {
  edgeObserver?.disconnect()
  const days = mountedDays.value
  if (!scrollContainer.value || days.length === 0) return
  const firstEl = dayHeaderEls[days[0].iso]
  const lastEl  = dayHeaderEls[days[days.length - 1].iso]
  edgeObserver = new IntersectionObserver(entries => {
    for (const e of entries) {
      if (!e.isIntersecting) continue
      if (e.target === firstEl) extendBefore()
      else if (e.target === lastEl) extendAfter()
    }
  }, { root: scrollContainer.value, rootMargin: `0px ${EDGE_LOOKAHEAD_PX}px 0px ${EDGE_LOOKAHEAD_PX}px`, threshold: 0 })
  if (firstEl) edgeObserver.observe(firstEl)
  if (lastEl && lastEl !== firstEl) edgeObserver.observe(lastEl)
}

const refreshObservers = () => { setupEdgeObserver() }

onUnmounted(() => {
  edgeObserver?.disconnect()
  resizeObserver.disconnect()
  scrollContainer.value?.removeEventListener('scroll', onContainerScroll)
  scrollContainer.value?.removeEventListener('touchstart', onTouchStart)
  scrollContainer.value?.removeEventListener('touchend', onTouchEnd)
  scrollContainer.value?.removeEventListener('touchcancel', onTouchEnd)
  if (scrollRafId !== null) cancelAnimationFrame(scrollRafId)
})

let isExtending = false

const extendBefore = async () => {
  if (isExtending) return
  isExtending = true
  const el = scrollContainer.value

  const newStart = new Date(loadedStart.value)
  newStart.setDate(newStart.getDate() - DAY_STEP)
  const sliceEnd = new Date(loadedStart.value)
  sliceEnd.setDate(sliceEnd.getDate() - 1)

  await Promise.all([
    fetchRange(localIso(newStart), localIso(sliceEnd)),
    fetchCancellationsRange(localIso(newStart), localIso(sliceEnd)),
  ])

  // Wait for any active touch to lift before touching the DOM/scrollLeft — see the note
  // by waitForTouchEnd's definition above.
  await waitForTouchEnd(el)

  const prevScrollWidth = el?.scrollWidth ?? 0
  loadedStart.value = newStart

  await nextTick()
  // Read scrollLeft fresh here rather than a value captured before the awaits above —
  // both fetchRange and waitForTouchEnd can take a while, and on mobile the user can
  // keep swiping the whole time. Correcting against a stale scrollLeft discarded that
  // motion and snapped back, which is what produced the "jumps around" symptom.
  if (el) el.scrollLeft += el.scrollWidth - prevScrollWidth  // preserve viewport when content is prepended
  refreshObservers()
  isExtending = false
}

const extendAfter = async () => {
  if (isExtending) return
  isExtending = true
  const newEnd = new Date(loadedEnd.value)
  newEnd.setDate(newEnd.getDate() + DAY_STEP)
  const sliceStart = new Date(loadedEnd.value)
  sliceStart.setDate(sliceStart.getDate() + 1)

  await Promise.all([
    fetchRange(localIso(sliceStart), localIso(newEnd)),
    fetchCancellationsRange(localIso(sliceStart), localIso(newEnd)),
  ])
  loadedEnd.value = newEnd

  await nextTick()
  refreshObservers()
  isExtending = false
}

const shiftDay = async delta => {
  const d = new Date(currentDayIso.value + 'T00:00:00')
  d.setDate(d.getDate() + delta)
  const iso = localIso(d)
  if (!dayHeaderEls[iso]) {
    await (delta < 0 ? extendBefore() : extendAfter())
  }
  navigateToDay(iso)
}
const prevDay = () => shiftDay(-1)
const nextDay = () => shiftDay(1)

const initialLoad = async centerIso => {
  isLoading.value = true
  try {
    const center = new Date(centerIso + 'T00:00:00')
    const start = new Date(center); start.setDate(start.getDate() - DAY_STEP)
    const end   = new Date(center); end.setDate(end.getDate() + DAY_STEP)
    loadedStart.value = start
    loadedEnd.value = end
    await Promise.all([
      fetchRange(localIso(start), localIso(end), { replace: true }),
      fetchCancellationsRange(localIso(start), localIso(end), { replace: true }),
      fetchTeachersOnce(),
    ])
  } catch {
    console.error('Failed to fetch calendar data')
  } finally {
    isLoading.value = false
  }
  currentDayIso.value = centerIso  // set before the DOM catches up so the label doesn't flash the old (now out-of-range) date
  await nextTick()
  if (!props.noAutoFit) fitHourHeightToContainer(scrollContainer.value, computedMaxHour.value - computedMinHour.value)
  navigateToDay(centerIso)
  scrollToRelevantTime(centerIso)
  refreshObservers()
}

// Arbitrarily-far jump (date picker / Today): reset the window rather than walking there a step at a time.
const jumpToDate = iso => {
  if (dayHeaderEls[iso]) {
    navigateToDay(iso)
    scrollToRelevantTime(iso)
  } else {
    initialLoad(iso)
  }
}
const ghostsForCol = (teacher, iso) =>
  [...ghosts.value, ...requestGhosts.value].filter(g => g.lesson_date === iso && g.teacher_name === teacher && matchesLocation(g))

const jumpToday = () => jumpToDate(todayIso)

const refresh = () => Promise.all([
  fetchRange(localIso(loadedStart.value), localIso(loadedEnd.value), { replace: true }),
  fetchCancellationsRange(localIso(loadedStart.value), localIso(loadedEnd.value), { replace: true }),
])

watch(lessonsChange, () => refresh())

onMounted(() => { initialLoad(props.initialDate || queryDateIso || todayIso) })

// ── Dynamic Y-axis ────────────────────────────────────────────────────────────
const hourRange = computed(() => {
  let min = 23, max = 0
  for (const day of activeDayGroups.value)
    for (const col of day.cols) {
      for (const block of col.availability || []) {
        if (block.start) min = Math.min(min, parseInt(block.start, 10))
        if (block.end) {
          const [h, m] = block.end.split(':').map(Number)
          max = Math.max(max, m > 0 ? h + 1 : h)
        }
      }
      for (const l of [...lessonsForCol(col.teacher, day.iso), ...ghostsForCol(col.teacher, day.iso)]) {
        const p = parseLessonTime(l.lesson_time)
        if (p) {
          min = Math.min(min, Math.max(0, p.h - 1))
          max = Math.max(max, Math.min(24, Math.ceil((p.h * 60 + p.m + (l.duration_minutes || 30)) / 60) + 1))
        }
      }
    }
  const lo = min === 23 ? 8 : min
  const hi = max === 0 ? lo + 5 : Math.max(max, lo + 5)
  return { min: lo, max: hi }
})
const computedMinHour = computed(() => hourRange.value.min)
const computedMaxHour = computed(() => hourRange.value.max)

const bodyHeight   = computed(() => (computedMaxHour.value - computedMinHour.value) * hourHeight.value)
const allHours     = computed(() => Array.from({ length: computedMaxHour.value - computedMinHour.value + 1 }, (_, i) => computedMinHour.value + i))
const displayHours = computed(() => allHours.value.filter(h => h < computedMaxHour.value))

// ── Day groups (each day's teacher sub-columns laid out sequentially) ───────
// Multi-teacher views (team calendar / admin day view) drop a teacher's column on days
// they have neither availability nor a lesson — avoids a wall of empty columns for
// teachers who only work 1-2 days/week. The locked-teacher (own dashboard) view is
// exempt: it's always exactly one column, and it must stay even on an "unavailable"
// day so the teacher can still see it and add an ad-hoc lesson. `span` keeps a minimum
// of 1 even when every teacher is filtered out for a day, so the day still renders as
// a (labelless) placeholder column instead of vanishing — see the "Empty day" v-else
// branches above, both in the teacher sub-header row and the body grid.
const activeDayGroups = computed(() => {
  let gridCol = 2
  return mountedDays.value.map(day => {
    const startCol = gridCol
    const names = props.lockedTeacher
      ? filteredTeachers.value
      : filteredTeachers.value.filter(name => {
          const t = teacherMap.value.get(name)
          return !!(t?.availability?.[day.weekdayLong]?.length) || lessonsForCol(name, day.iso).length > 0 || ghostsForCol(name, day.iso).length > 0
        })
    const cols = names
      .map((name, i) => ({
        teacher:      name,
        availability: teacherMap.value.get(name)?.availability?.[day.weekdayLong] || null,
        gridCol:      startCol + i,
      }))
    const span = Math.max(cols.length, 1)
    gridCol += span
    return { ...day, cols, gridColStart: startCol, gridColSpan: span }
  })
})
// Day widths change with the teacher filter — recompute the visible range label.
watch(activeDayGroups, () => onContainerScroll(), { flush: 'post' })


// Fixed-width columns (not stretched to fill the viewport) — total content width
// spans all mounted days, which is what makes horizontal day-paging by scroll work.
const gridCols = computed(() => {
  const parts = [`${TIME_COL_WIDTH}px`]
  for (const day of activeDayGroups.value) {
    const n = Math.max(day.cols.length, 1)
    for (let i = 0; i < n; i++) parts.push(`${COL_WIDTH}px`)
  }
  return parts.join(' ')
})

</script>

<style scoped>
/* Day header date label: below sm (640px), the day's cell can be much wider than the
   viewport (many teacher columns selected on a narrow screen), so the label needs the
   sticky + viewport-relative `left` hack (see the template comment above) to stay
   visible while scrolled. At sm and up, the row fits the viewport without horizontal
   scroll, so the sticky tracking isn't needed — instead just center the label within
   its own (possibly multi-column-wide) cell via flex auto-margins. */
.dv-day-header-inner {
  left: var(--dv-header-left);
}

@media (min-width: 640px) {
  .dv-day-header-inner {
    position: static;
    left: auto;
    margin-left: auto;
    margin-right: auto;
  }
}
</style>
